import axios from '@/utils/axiosInstance';
import type {
  AdminCreatePreceptorPayload,
  BackendPreceptor,
  GetAllPreceptorsParams,
  PaginatedPreceptorsResponse,
  PreceptorAdminFilters,
} from '@/services/preceptor-service';
import type { ImgApplicationPayload } from '@/types/img';
import type { PreceptorSiteAssignment } from '@/types/preceptor';

/**
 * Public: fetch IMG preceptors for the IMG search/rotation pages.
 * Returns the SAME shape as getAllPreceptors so the existing transform is reused.
 */
export async function getAllImgPreceptors(params?: GetAllPreceptorsParams): Promise<BackendPreceptor[]> {
  const queryParams: Record<string, string> = {};
  if (params?.university_name) queryParams.university_name = params.university_name;
  if (params?.start_date) queryParams.start_date = params.start_date;
  if (params?.end_date) queryParams.end_date = params.end_date;

  const res = await axios.get<{ data: BackendPreceptor[]; message: string }>(`/public/img-preceptors`, {
    params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
  });
  return res.data.data;
}

/** Submit an IMG rotation application. Public — no account required. */
export async function submitImgApplication(payload: ImgApplicationPayload) {
  const res = await axios.post(`/public/img-applications`, payload);
  return res.data;
}

/* ----------------------------- Admin ----------------------------- */

export async function getImgPreceptors(params?: { page?: number; per_page?: number; search?: string; status?: string }) {
  const res = await axios.get(`/admin/img-preceptors`, { params });
  return res.data;
}

/**
 * Admin: paginated IMG preceptors in the SAME shape as getPaginatedPreceptors so the
 * shared admin Preceptors list page/table can be reused unchanged for the IMG track.
 */
export async function getPaginatedImgPreceptors(
  page = 1,
  perPage = 15,
  filters: PreceptorAdminFilters = {},
): Promise<PaginatedPreceptorsResponse> {
  const params: Record<string, unknown> = { page, per_page: perPage };
  const trimmedSearch = filters.search?.trim();
  if (trimmedSearch) params.search = trimmedSearch;
  if (filters.status && filters.status !== 'all') params.status = filters.status;
  // Additional column filters — only sent when set.
  if (filters.specialties && filters.specialties.length > 0) params.specialties = filters.specialties;
  if (filters.states && filters.states.length > 0) params.states = filters.states;
  if (filters.city?.trim()) params.city = filters.city.trim();
  if (filters.rotationCode?.trim()) params.rotation_code = filters.rotationCode.trim();

  const res = await axios.get<{
    data: BackendPreceptor[];
    pagination: { current_page: number; per_page: number; total: number; last_page: number };
    stats?: { verified: number; unverified: number };
    message: string;
  }>(`/admin/img-preceptors`, { params });
  const payload = res.data;

  return {
    data: payload.data as BackendPreceptor[],
    links: { first: '', last: '', prev: null, next: null },
    meta: {
      current_page: payload.pagination.current_page,
      from: payload.pagination.current_page === 1 ? 1 : (payload.pagination.current_page - 1) * payload.pagination.per_page + 1,
      last_page: payload.pagination.last_page,
      links: [],
      path: '',
      per_page: payload.pagination.per_page,
      to: Math.min(payload.pagination.total, payload.pagination.current_page * payload.pagination.per_page),
      total: payload.pagination.total,
    },
    stats: payload.stats,
  };
}

/** Admin: load an IMG preceptor's sites + schedules (same shape as NP getAdminPreceptorSites). */
export async function getImgPreceptorSites(id: string | number): Promise<PreceptorSiteAssignment[]> {
  const res = await axios.get(`/admin/img-preceptors/${encodeURIComponent(id)}/sites`);
  return res.data.data ?? [];
}

/** Admin: save an IMG preceptor's sites + schedules. `sites` mirrors the NP upsert payload. */
export async function saveImgPreceptorSites(
  id: string | number,
  sites: unknown[],
): Promise<PreceptorSiteAssignment[]> {
  const res = await axios.put(`/admin/img-preceptors/${encodeURIComponent(id)}/sites`, { sites });
  return res.data.data ?? [];
}

export interface ImgPreceptorBookingRow {
  id: number;
  application_number: string;
  status: string;
  applicant_name: string | null;
  applicant_email: string | null;
  specialty: string | null;
  top_specialty: string | null;
  created_at: string | null;
}

/** Admin: applications (ImgBooking rows) tied to an IMG preceptor for the Booking Schedule tab. */
export async function getImgPreceptorBookings(id: string | number): Promise<ImgPreceptorBookingRow[]> {
  const res = await axios.get(`/admin/img-preceptors/${encodeURIComponent(id)}/bookings`);
  return res.data.data ?? [];
}

/**
 * Distinct site-state values actually present among IMG preceptors, used to
 * build the State filter dropdown (mirrors getPreceptorStates for the NP list).
 */
export async function getImgPreceptorStates(): Promise<string[]> {
  const res = await axios.get<{ data: string[] }>(`/admin/img-preceptors/states`);
  return Array.isArray(res.data?.data) ? res.data.data : [];
}

/** Admin: remove an availability block from every IMG preceptor. */
export async function bulkRemoveImgAvailability(payload: { type: 'season' | 'restricted'; value: string }) {
  const res = await axios.post(`/admin/img-preceptors/availability/bulk-remove`, payload);
  return res.data;
}

/** Restore a soft-deleted IMG preceptor. */
export async function restoreImgPreceptor(id: string | number) {
  const res = await axios.post(`/admin/img-preceptors/${encodeURIComponent(id)}/restore`);
  return res.data;
}

/**
 * Admin create — accepts the NP-shaped AdminCreatePreceptorPayload emitted by the shared
 * create form and maps it onto the flatter IMG create endpoint.
 */
export async function createImgAdminPreceptor(
  payload: AdminCreatePreceptorPayload,
): Promise<{ id: string | number }> {
  const { site, preceptor } = payload;
  const created = await createImgPreceptor({
    sync_to_pipedrive: payload.sync_to_pipedrive ?? false,
    first_name: preceptor.first_name,
    last_name: preceptor.last_name || null,
    titles: preceptor.titles ?? null,
    specialty_id: preceptor.specialty_id ?? preceptor.specialty_ids?.[0] ?? null,
    status: preceptor.status ?? 'verified',
    bio: preceptor.bio ?? null,
    years_experience: preceptor.years_experience ?? null,
    license_number: preceptor.license_number ?? null,
    license_state: preceptor.license_state ?? null,
    rotation_setting: preceptor.rotation_setting ?? null,
    accepts_tele: preceptor.accepts_tele ?? false,
    accepts_in_person: preceptor.accepts_in_person ?? false,
    site: {
      name: site.location_name,
      address: site.street_address,
      city: site.city_municipality,
      state: site.province,
      zip: site.zip_code,
      latitude: site.latitude,
      longitude: site.longitude,
    },
  });
  return { id: (created as { id: string | number }).id };
}

export async function getImgPreceptor(id: string | number) {
  const res = await axios.get(`/admin/img-preceptors/${encodeURIComponent(id)}`);
  return res.data.data;
}

export async function createImgPreceptor(payload: Record<string, unknown>) {
  const res = await axios.post(`/admin/img-preceptors`, payload);
  return res.data.data;
}

export async function updateImgPreceptor(id: string | number, payload: Record<string, unknown>) {
  const res = await axios.put(`/admin/img-preceptors/${encodeURIComponent(id)}`, payload);
  return res.data.data;
}

export async function deleteImgPreceptor(id: string | number) {
  await axios.delete(`/admin/img-preceptors/${encodeURIComponent(id)}`);
}

export async function updateImgPreceptorStatus(id: string | number, status: string) {
  const res = await axios.put(`/admin/img-preceptors/${encodeURIComponent(id)}/status`, { status });
  return res.data.data;
}

export async function getImgApplications(params?: { page?: number; per_page?: number; search?: string; status?: string }) {
  const res = await axios.get(`/admin/img-applications`, { params });
  return res.data;
}

export async function getImgApplication(id: string | number) {
  const res = await axios.get(`/admin/img-applications/${encodeURIComponent(id)}`);
  return res.data.data;
}

export async function updateImgApplicationStatus(id: string | number, status: string, adminNotes?: string) {
  const res = await axios.put(`/admin/img-applications/${encodeURIComponent(id)}/status`, { status, admin_notes: adminNotes });
  return res.data.data;
}

export async function getImgStudents(params?: { page?: number; per_page?: number; search?: string }) {
  const res = await axios.get(`/admin/img-students`, { params });
  return res.data;
}
